import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { api } from "@/api";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format";

export interface ProductOption {
  id: number;
  name: string;
  sku?: string | null;
  unit?: string | null;
  unit_price?: number;
  tax_rate?: number | null;
}

interface ProductComboboxProps {
  value: string;
  productId: number | null;
  onTextChange: (value: string) => void;
  onSelect: (product: ProductOption) => void;
  /**
   * Fired when the user picks the pinned "— None —" row to clear the row.
   * Callers should reset BOTH the display text and the associated productId.
   * When omitted the combobox falls back to clearing the text via
   * `onTextChange("")`, but wiring this explicitly is preferred so the
   * productId is reset too.
   */
  onClear?: () => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  "aria-label"?: string;
  /**
   * When true, this row is required to end up with a catalog `productId`.
   * Free-text is still accepted as the user types (so they can search), but
   * the parent form is expected to block save unless a product is picked.
   * The combobox itself only uses this for help-text wording and to expose
   * the state via `aria-invalid` when `invalid` is also set.
   */
  strict?: boolean;
  /**
   * Visual / a11y signal that the current row failed strict validation. When
   * true the input gains a red ring and exposes `aria-invalid="true"` so
   * inline error messages can target this field.
   */
  invalid?: boolean;
  /**
   * Optional pre-loaded product catalog. When supplied the combobox skips its
   * own fetch and filters this list client-side. Parents that already hold a
   * product list (e.g. QuotationForm) should pass it here so we don't issue
   * a duplicate request.
   */
  options?: ProductOption[];
}

// Cap the visible suggestion list so a 5000-row catalog doesn't render 5000
// DOM nodes. Anything beyond this is hidden behind a "scroll for more" hint.
const MAX_VISIBLE = 200;
const CATALOG_PAGE_SIZE = 1000;

interface ProductListResponse {
  products: ProductOption[];
  total?: number;
}

// Module-level cache: the products endpoint returns the same payload for
// every combobox instance on a page, so a single fetch is reused across all
// rows / forms within a session. Refresh by reloading the route.
let cachedCatalog: ProductOption[] | null = null;
let inflightCatalog: Promise<ProductOption[]> | null = null;

async function loadCatalog(): Promise<ProductOption[]> {
  if (cachedCatalog) return cachedCatalog;
  if (inflightCatalog) return inflightCatalog;
  inflightCatalog = (async () => {
    try {
      const all: ProductOption[] = [];
      let offset = 0;
      let total = 0;

      while (true) {
        const params = new URLSearchParams({
          limit: String(CATALOG_PAGE_SIZE),
          offset: String(offset),
        });
        const res = await api.get<ProductListResponse>(
          `/products?${params.toString()}`,
        );
        const page = res.products ?? [];
        all.push(...page);
        total = Number(res.total ?? all.length);

        if (page.length < CATALOG_PAGE_SIZE || all.length >= total) break;
        offset += page.length;
      }

      cachedCatalog = all;
      return cachedCatalog;
    } catch {
      cachedCatalog = [];
      return cachedCatalog;
    } finally {
      inflightCatalog = null;
    }
  })();
  return inflightCatalog;
}

function matchesQuery(p: ProductOption, q: string) {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (p.name?.toLowerCase().includes(needle)) return true;
  if (p.sku?.toLowerCase().includes(needle)) return true;
  if (p.unit?.toLowerCase().includes(needle)) return true;
  return false;
}

export default function ProductCombobox({
  value,
  productId,
  onTextChange,
  onSelect,
  onClear,
  disabled,
  className,
  placeholder = "Click to choose, or type to filter…",
  "aria-label": ariaLabel = "Product",
  strict = false,
  invalid = false,
  options,
}: ProductComboboxProps) {
  const listboxId = useId();
  const optionIdPrefix = useId();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  // `justPicked` prevents the dropdown from immediately reopening after a
  // selection (focus stays in the input so the user can keep tabbing, but
  // we don't want the list to flash back open). Cleared on the next blur,
  // arrow-key press, or text change.
  const justPickedRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [catalog, setCatalog] = useState<ProductOption[]>(
    options ?? cachedCatalog ?? [],
  );
  const [loading, setLoading] = useState(
    !options && !cachedCatalog,
  );
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: "below" | "above";
  } | null>(null);

  // If the parent passes `options`, keep state in sync with it.
  useEffect(() => {
    if (options) setCatalog(options);
  }, [options]);

  // Lazy-load the global catalog once if the parent didn't supply one.
  useEffect(() => {
    if (options) return;
    if (cachedCatalog) {
      setCatalog(cachedCatalog);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadCatalog().then((list) => {
      if (cancelled) return;
      setCatalog(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [options]);

  // Filter client-side against the full paged catalog; MAX_VISIBLE keeps the
  // rendered DOM small even when the catalogue has thousands of rows.
  const filtered = useMemo(() => {
    const q = value.trim();
    const list = q ? catalog.filter((p) => matchesQuery(p, q)) : catalog;
    return list.slice(0, MAX_VISIBLE);
  }, [catalog, value]);

  const totalMatches = useMemo(() => {
    const q = value.trim();
    if (!q) return catalog.length;
    return catalog.filter((p) => matchesQuery(p, q)).length;
  }, [catalog, value]);

  const positionDropdown = useCallback(() => {
    const trigger = inputRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const margin = 8;
    const gap = 4;
    const minHeight = 120;

    const spaceBelow = vh - rect.bottom - margin - gap;
    const spaceAbove = rect.top - margin - gap;

    const placement: "below" | "above" =
      spaceBelow >= minHeight || spaceBelow >= spaceAbove ? "below" : "above";

    const maxHeight = Math.max(
      minHeight,
      placement === "below" ? spaceBelow : spaceAbove,
    );

    const listRect = listRef.current?.getBoundingClientRect();
    const measured = listRect ? listRect.height : maxHeight;
    const usedHeight = Math.min(measured, maxHeight);

    const top =
      placement === "below"
        ? rect.bottom + gap
        : rect.top - usedHeight - gap;

    let left = rect.left;
    const maxLeft = Math.max(margin, vw - rect.width - margin);
    left = Math.min(Math.max(margin, left), maxLeft);

    // The menu is rendered with `position: fixed`, so its coordinates are
    // already viewport-relative — exactly what getBoundingClientRect() returns.
    // Do NOT add window.scrollX/scrollY here: that would push the menu off by
    // the page's scroll amount (it only made sense for position: absolute), so
    // on any scrolled page the dropdown landed below the visible area.
    setCoords({
      top,
      left,
      width: rect.width,
      maxHeight,
      placement,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    positionDropdown();
    const onScrollOrResize = () => positionDropdown();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, positionDropdown]);

  // Re-measure after the list mounts / its filtered contents change so the
  // "above" placement uses the actual rendered height instead of the
  // worst-case max-height.
  useEffect(() => {
    if (!open) return;
    positionDropdown();
  }, [open, filtered, loading, positionDropdown]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (inputRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Keep the highlighted option scrolled into view.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLLIElement>(
      `[data-option-index="${activeIndex}"]`,
    );
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  // Reset highlight whenever the visible list changes.
  useEffect(() => {
    setActiveIndex(filtered.length ? 0 : -1);
  }, [filtered]);

  function pickOption(option: ProductOption) {
    onSelect(option);
    setOpen(false);
    setActiveIndex(-1);
    justPickedRef.current = true;
  }

  // Clear the row back to its empty/unselected state via the pinned
  // "— None —" row. Prefer the explicit onClear so the caller can also reset
  // its productId; otherwise fall back to clearing the text.
  function clearSelection() {
    if (onClear) {
      onClear();
    } else {
      onTextChange("");
    }
    setOpen(false);
    setActiveIndex(-1);
    justPickedRef.current = true;
  }

  function openMenu() {
    if (disabled) return;
    justPickedRef.current = false;
    setOpen(true);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      justPickedRef.current = false;
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((idx) =>
        filtered.length ? (idx + 1) % filtered.length : -1,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      justPickedRef.current = false;
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((idx) =>
        filtered.length ? (idx - 1 + filtered.length) % filtered.length : -1,
      );
    } else if (event.key === "Enter") {
      if (open && activeIndex >= 0 && filtered[activeIndex]) {
        event.preventDefault();
        pickOption(filtered[activeIndex]!);
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
      }
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  }

  const activeId =
    open && activeIndex >= 0 ? `${optionIdPrefix}-${activeIndex}` : undefined;

  return (
    <div className={cn("relative w-full", className)}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={activeId}
        aria-invalid={invalid || undefined}
        aria-required={strict || undefined}
        autoComplete="off"
        spellCheck={false}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        data-product-id={productId ?? ""}
        data-strict={strict ? "true" : undefined}
        onChange={(e) => {
          justPickedRef.current = false;
          onTextChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          if (justPickedRef.current) return;
          setOpen(true);
        }}
        onClick={openMenu}
        onKeyDown={handleKeyDown}
        className={cn(
          "h-8 w-full rounded-md border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          invalid
            ? "border-destructive/70 ring-1 ring-destructive/40 focus-visible:ring-destructive/70"
            : "border-input",
          disabled ? "opacity-60 cursor-not-allowed" : "",
        )}
      />
      {open && coords
        ? createPortal(
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={`${ariaLabel} suggestions`}
              data-placement={coords.placement}
              className="fixed z-[60] overflow-y-auto overscroll-contain rounded-md border bg-popover text-popover-foreground shadow-xl backdrop-blur"
              style={{
                top: coords.top,
                left: coords.left,
                width: coords.width,
                maxHeight: coords.maxHeight,
              }}
            >
              <li
                role="option"
                aria-selected={false}
                // onMouseDown so the click fires before the input blurs.
                onMouseDown={(event) => {
                  event.preventDefault();
                  clearSelection();
                }}
                className="cursor-pointer border-b px-3 py-2 text-xs italic text-muted-foreground hover:bg-muted"
              >
                — None —
              </li>
              {loading ? (
                <li className="px-3 py-2 text-xs text-muted-foreground">
                  Loading products…
                </li>
              ) : null}
              {!loading && filtered.length === 0 && catalog.length === 0 ? (
                <li className="px-3 py-2 text-xs text-muted-foreground">
                  No products yet. Add one on the Inventory page first.
                </li>
              ) : null}
              {!loading && filtered.length === 0 && catalog.length > 0 ? (
                <li className="px-3 py-2 text-xs text-muted-foreground">
                  No products match “{value.trim()}”.
                </li>
              ) : null}
              {!loading && filtered.length > 0 ? (
                <li
                  className="sticky top-0 z-[1] bg-popover px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground border-b"
                  aria-hidden
                >
                  {totalMatches > MAX_VISIBLE
                    ? `Showing ${MAX_VISIBLE} of ${totalMatches} — keep typing to narrow`
                    : `${totalMatches} product${totalMatches === 1 ? "" : "s"}`}
                </li>
              ) : null}
              {!loading &&
                filtered.map((p, idx) => {
                  const isActive = idx === activeIndex;
                  return (
                    <li
                      key={p.id}
                      id={`${optionIdPrefix}-${idx}`}
                      role="option"
                      aria-selected={isActive}
                      data-option-index={idx}
                      onMouseEnter={() => setActiveIndex(idx)}
                      // Use onMouseDown so the click fires before the input
                      // loses focus (which would otherwise close the listbox).
                      onMouseDown={(event) => {
                        event.preventDefault();
                        pickOption(p);
                      }}
                      className={cn(
                        "flex items-center justify-between gap-3 px-3 py-2 text-xs cursor-pointer",
                        isActive
                          ? "bg-accent text-accent-foreground"
                          : "text-popover-foreground hover:bg-muted",
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{p.name}</div>
                        {p.sku ? (
                          <div className="truncate text-[10px] text-muted-foreground">
                            SKU: {p.sku}
                            {p.unit ? ` · ${p.unit}` : ""}
                          </div>
                        ) : p.unit ? (
                          <div className="truncate text-[10px] text-muted-foreground">
                            {p.unit}
                          </div>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right font-mono text-[11px] text-primary">
                        {formatNumber(Number(p.unit_price ?? 0))}
                      </div>
                    </li>
                  );
                })}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
